import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", organisation: "", interest: "Executive Briefing", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We'll be in touch soon.");
      setForm(empty);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please email research@bigibila.com.au directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-surface1">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">Enquire</p>
          <h2 data-testid="contact-heading" className="font-serif text-3xl md:text-5xl tracking-tight mb-8">
            Engage <em className="text-ember">Bigibila.</em>
          </h2>
          <p className="text-stone2 leading-relaxed mb-10 max-w-md">
            Tell us about your organisation, your systems and what you need to
            understand. Every engagement begins with a scoped conversation.
          </p>
          <div className="space-y-5 font-mono text-sm">
            <a href="mailto:research@bigibila.com.au" data-testid="contact-email-info" className="link-underline block text-bone hover:text-ember transition-colors duration-300 w-fit">
              research@bigibila.com.au
            </a>
            <a href="mailto:liana@bigibila.com.au" data-testid="contact-email-liana" className="link-underline block text-bone hover:text-ember transition-colors duration-300 w-fit">
              liana@bigibila.com.au
            </a>
            <a href="tel:0466943229" data-testid="contact-phone" className="link-underline block text-bone hover:text-ember transition-colors duration-300 w-fit">
              0466 943 229
            </a>
          </div>
          <div className="quill-rule mt-12 w-2/3" />
        </div>

        <motion.form
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          onSubmit={submit}
          data-testid="enquiry-form"
          className="lg:col-span-7 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="name">Name *</label>
              <input id="name" data-testid="enquiry-name-input" className="field-dark w-full px-4 py-3.5 text-sm" value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="email">Email *</label>
              <input id="email" type="email" data-testid="enquiry-email-input" className="field-dark w-full px-4 py-3.5 text-sm" value={form.email} onChange={set("email")} placeholder="you@organisation.au" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="organisation">Organisation</label>
              <input id="organisation" data-testid="enquiry-org-input" className="field-dark w-full px-4 py-3.5 text-sm" value={form.organisation} onChange={set("organisation")} placeholder="Your organisation" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="interest">Interested in</label>
              <select id="interest" data-testid="enquiry-interest-select" className="field-dark w-full px-4 py-3.5 text-sm" value={form.interest} onChange={set("interest")}>
                <option>Executive Briefing</option>
                <option>Governance Assessment</option>
                <option>Research Commission</option>
                <option>Licensed Bigibila Services and Outputs</option>
                <option>Workshop</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="message">Message *</label>
            <textarea id="message" rows={6} data-testid="enquiry-message-input" className="field-dark w-full px-4 py-3.5 text-sm resize-none" value={form.message} onChange={set("message")} placeholder="Tell us about your organisation, your systems, and what you need to understand." />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-btn"
            className="font-mono text-sm uppercase tracking-[0.2em] px-10 py-4 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Send enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
